import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Select } from '@tencent/tea-component';
import { usePortalStore } from '@src/store';
import { useLocation } from 'react-router-dom';
import { AddCircleIcon } from '@tencent/tpm-audio-video-react-icons';
import { parseApp } from '@src/utils/parser';

// 店铺选项类型
export interface MerchantOption {
  value: string;
  text: string;
  isDefault?: boolean;
  [key: string]: any;
}

// 扩展菜单类型，支持 hideTopMerchantList 属性
interface ExtendedMenu extends Application.Menu {
  hideTopMerchantList?: boolean;
}

// 彩色日志打印
const logEvent = (eventName: string, data: unknown) => {
  console.log(
    '%c[AppSelectPlanB] %c' + eventName,
    'color: pink; font-weight: bold;',
    'color: hotpink;',
    data,
  );
};

export const AppSelectPlanB = () => {
  // 店铺列表（从事件获取，初始为空）
  const [merchantList, setMerchantList] = useState<MerchantOption[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<string>('');
  // 菜单完整信息
  const [menuInfo, setMenuInfo] = useState<ExtendedMenu | null>(null);
  // 是否允许展示店铺列表（由 canShowMerchantList 事件控制）
  // todo
  const [canShowList, setCanShowList] = useState<boolean>(false);
  const isFirstMerchantList = useRef(true);
  const app = usePortalStore(state => state.app);
  const menu = usePortalStore(state => state.menu);
  const location = useLocation();

  // 根据 menu 获取菜单完整信息
  useEffect(() => {
    if (!app || !menu) {
      setMenuInfo(null);
      return;
    }
    parseApp(app).then(appInfo => {
      if (appInfo?.menus) {
        // 查找匹配的菜单项
        const matched = appInfo.menus.find(m => {
          // 直接匹配一级菜单
          if (m.path === menu) return true;
          // 匹配二级菜单（一级为 /）
          if (m.path === '/' && m.submenus?.some(s => s.path === menu)) {
            return true;
          }
          return false;
        });
        setMenuInfo(matched || null);
        // logEvent('menuInfoMatched', matched);
      }
    });
  }, [app, menu]);

  // 监听 merchantList、operatingMerchant 事件
  useEffect(() => {
    // 事件原始数据类型
    interface MerchantEventData {
      id: string;
      name: string;
      isDefault: boolean;
    }

    // 处理 merchantList 事件：更新下拉列表，只有第一次才设置默认选中
    const handleMerchantList = (
      e: CustomEvent<{ data: MerchantEventData[] }>,
    ) => {
      const rawData = e.detail?.data || [];
      logEvent('基座拿到店铺列表 merchantList', e);

      // 转换数据格式：id -> value, name -> text
      const options: MerchantOption[] = rawData.map(item => ({
        value: item.id,
        text: item.name,
        shownName: item.name,
        isDefault: item.isDefault,
      }));

      setMerchantList(options);
      // 只有第一次接收事件时才设置选中项
      if (isFirstMerchantList.current) {
        isFirstMerchantList.current = false;
        // 查找 isDefault 为 true 的项作为默认选中
        const defaultItem = options.find(item => item.isDefault);
        if (defaultItem) {
          setSelectedMerchant(defaultItem.value);
        } else if (options.length > 0) {
          // 如果没有默认项，选中第一个
          setSelectedMerchant(options[0].value);
        }
      }
    };

    // 处理 operatingMerchant 事件：添加或编辑店铺
    const handleOperatingMerchant = (
      e: CustomEvent<{ type: 'add' | 'edit'; data: MerchantEventData }>,
    ) => {
      const { type, data: rawData } = e.detail || {};
      logEvent('operatingMerchant', e);
      if (!rawData) return;

      // 转换数据格式：id -> value, name -> text
      const data: MerchantOption = {
        value: rawData.id,
        text: rawData.name,
        isDefault: rawData.isDefault,
      };

      if (type === 'add') {
        // 添加：将数据放到列表第一条并选中
        setMerchantList(prev => [data, ...prev]);
        setSelectedMerchant(data.value);
      } else if (type === 'edit') {
        // 编辑：找到对应项并修改
        setMerchantList(prev =>
          prev.map(item => (item.value === data.value ? data : item)),
        );
      }
    };

    // 处理 canShowMerchantList 事件：收到事件即可展示店铺列表
    const handleCanShowMerchantList = () => {
      logEvent('基座拿到 canShowMerchantList 事件', true);
      setCanShowList(true);
    };

    document.body.addEventListener(
      'merchantList',
      handleMerchantList as EventListener,
    );
    document.body.addEventListener(
      'operatingMerchant',
      handleOperatingMerchant as EventListener,
    );
    document.body.addEventListener(
      'canShowMerchantList',
      handleCanShowMerchantList as EventListener,
    );
    return () => {
      document.body.removeEventListener(
        'merchantList',
        handleMerchantList as EventListener,
      );
      document.body.removeEventListener(
        'operatingMerchant',
        handleOperatingMerchant as EventListener,
      );
      document.body.removeEventListener(
        'canShowMerchantList',
        handleCanShowMerchantList as EventListener,
      );
    };
  }, [location.pathname]);

  // 店铺切换时发送事件
  const handleMerchantChange = useCallback((value: string) => {
    setSelectedMerchant(value);
    const switchMerchantEvent = new CustomEvent('switchMerchant', {
      detail: { value },
    });
    logEvent('基座发送 switchMerchant 事件', { id: value });
    document.body?.dispatchEvent(switchMerchantEvent);
  }, []);

  // 点击新增店铺发送事件
  const handleAddMerchant = useCallback(() => {
    const addMerchantEvent = new CustomEvent('addMerchant');
    logEvent('基座发送addMerchant事件', null);
    document.body?.dispatchEvent(addMerchantEvent);
  }, []);

  // 计算是否显示 Select
  const shouldShowSelect = useMemo(() => {
    // canShowMerchantList 事件控制的 canShowList 为 false 时不显示
    if (!canShowList) {
      return false;
    }
    // menuInfo 的 hideTopMerchantList 为 true 时不显示
    if (menuInfo?.hideTopMerchantList) {
      return false;
    }
    return true;
  }, [canShowList, menuInfo?.hideTopMerchantList]);

  return (
    <>
      {shouldShowSelect ? (
        <Select
          className="is-v2-nav"
          overlayClassName="is-v2-nav"
          appearance="button"
          placement="bottom-end"
          // placeholder={t('Loading...')}
          options={merchantList}
          value={selectedMerchant}
          onChange={value => handleMerchantChange(value)}
          searchable
          style={{ minWidth: 250 }}
          footer={
            <a onClick={handleAddMerchant}>
              <AddCircleIcon color="#1C66E5" size={16} />
              Add Another Business
            </a>
          }
        />
      ) : null}
    </>
  );
};
